import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { toast } from 'react-toastify';
import { Modal, Box, Typography, Button } from '@mui/material';
import { encryptData } from '../../utils/cryptoUtils';
import * as leadActions from '../../actions/leadActions';

const PAGE_SIZE = 25;

const RcfromChessicReport = (props) => {
  const { actions } = props;

  const [tableData, setTableData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);

  const fetchReport = (page = 1, search = '') => {
    const params = encryptData({
      start: (page - 1) * PAGE_SIZE,
      length: PAGE_SIZE,
      search,
    });

    const response = actions.getRcFromChassisReport(params);

    response
      .then((result) => {
        if (result.data.success === 1) {
          setTableData(result.data.data || []);
          setTotalRecords(result.data.total || 0);
        } else {
          toast.error(result.data.message || 'Failed to fetch data');
        }
      })
      .catch((error) => {
        toast.error('Something went wrong');
      });
  };

  useEffect(() => {
    fetchReport(currentPage, searchText);
    // eslint-disable-next-line
  }, [currentPage]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchReport(1, searchText);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  const handleOpenResult = (row) => {
    setSelectedResult(row);
    setOpenModal(true);
  };

  const handleCloseResult = () => {
    setOpenModal(false);
    setSelectedResult(null);
  };

  const totalPages = Math.ceil(totalRecords / PAGE_SIZE);

  const getResultObj = (row) => {
    const decoded = row.api_response_decoded || {};
    return decoded?.data?.result ? decoded.data.result : decoded?.result ? decoded.result : {};
  };

  return (
    <div className="container-fluid">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <Typography variant="h4">RC From Chassis Report</Typography>
      </div>

      <div className="row mb-3">
        <div className="col-md-4">
          <input
            type="text"
            className="form-control"
            placeholder="Search by Chassis Number"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSearch();
            }}
          />
        </div>
        <div className="col-md-2">
          <Button variant="contained" fullWidth onClick={handleSearch}>
            Search
          </Button>
        </div>
        <div className="col-md-6 d-flex align-items-center justify-content-end">
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Total Records: {totalRecords}
          </Typography>
        </div>
      </div>

      <div className="table-wrap table-responsive">
        <table className="table table-hover table-bordered">
          <thead>
            <tr>
              <th>#</th>
              <th>Chassis Number</th>
              <th>Created By</th>
              <th>Created At</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {tableData.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center">
                  No data found
                </td>
              </tr>
            ) : (
              tableData.map((item, index) => (
                <tr key={item.id}>
                  <td>{(currentPage - 1) * PAGE_SIZE + index + 1}</td>
                  <td>{item.chassis_number}</td>
                  <td>{item.createdByName || '-'}</td>
                  <td>{formatDateTime(item.created_at)}</td>
                  <td>
                    <Button size="small" variant="contained" color="primary" onClick={() => handleOpenResult(item)}>
                      Open
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="d-flex align-items-center justify-content-between mt-3">
          <small className="text-muted">
            Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, totalRecords)} of{' '}
            {totalRecords} records
          </small>
          <ul className="pagination mb-0">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setCurrentPage(1)}>
                «
              </button>
            </li>
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setCurrentPage((p) => p - 1)}>
                ‹
              </button>
            </li>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === '...' ? (
                  <li key={`ellipsis-${idx}`} className="page-item disabled">
                    <span className="page-link">…</span>
                  </li>
                ) : (
                  <li key={p} className={`page-item ${currentPage === p ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => setCurrentPage(p)}>
                      {p}
                    </button>
                  </li>
                )
              )}

            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setCurrentPage((p) => p + 1)}>
                ›
              </button>
            </li>
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setCurrentPage(totalPages)}>
                »
              </button>
            </li>
          </ul>
        </div>
      )}

      {/* Result Modal */}
      <Modal open={openModal} onClose={handleCloseResult}>
        <Box
          sx={{
            width: '70%',
            maxHeight: '85vh',
            overflowY: 'auto',
            bgcolor: '#fff',
            p: 3,
            borderRadius: 2,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="m-0">Chassis Result - {selectedResult?.chassis_number}</h5>
            <Button variant="outlined" color="error" size="small" onClick={handleCloseResult}>
              Close
            </Button>
          </div>

          {selectedResult && (
            <div className="row">
              {(() => {
                const result = getResultObj(selectedResult);
                if (!result || Object.keys(result).length === 0) {
                  return <p>No result data found.</p>;
                }
                return (
                  <>
                    <div className="col-md-6">
                      <ul>
                        <li>
                          <strong>Chassis Number : </strong> {result.chassis_number}
                        </li>
                        <li>
                          <strong>Registration Number : </strong> {result.registration_number}
                        </li>
                        <li>
                          <strong>Owner Name : </strong> {result.user_name}
                        </li>
                        <li>
                          <strong>Vehicle Make/Model : </strong> {result.vehicle_make_model}
                        </li>
                        <li>
                          <strong>Fuel Type : </strong> {result.vehicle_fuel_description}
                        </li>
                        <li>
                          <strong>Fitness Upto : </strong> {result.fit_upto}
                        </li>
                        <li>
                          <strong>Registration Date : </strong> {result.registration_date}
                        </li>
                        <li>
                          <strong>Vehicle Age : </strong> {result.vehicle_age}
                        </li>
                        <li>
                          <strong>Insurance Policy No : </strong> {result.insurance?.policyNumber}
                        </li>
                        <li>
                          <strong>Insurance Company : </strong> {result.insurance?.company}
                        </li>
                        <li>
                          <strong>Insurance Valid Till : </strong> {result.insurance?.validTill}
                        </li>
                        <li>
                          <strong>Owner Address : </strong> {result.user_present_address}
                        </li>
                      </ul>
                    </div>
                    <div className="col-md-6">
                      <ul>
                        <li>
                          <strong>Father Name : </strong> {result.father_name}
                        </li>
                        <li>
                          <strong>Financer : </strong> {result.financer}
                        </li>
                        <li>
                          <strong>Vehicle category : </strong> {result.vehicle_category}
                        </li>
                        <li>
                          <strong>Vehicle class description : </strong> {result.vehicle_class_description}
                        </li>
                        <li>
                          <strong>Vehicle cubic capacity : </strong> {result.vehicle_cubic_capacity}
                        </li>
                        <li>
                          <strong>Vehicle gross weight : </strong> {result.vehicle_gross_weight}
                        </li>
                        <li>
                          <strong>Vehicle maker description : </strong> {result.vehicle_maker_description}
                        </li>
                        <li>
                          <strong>Vehicle manufactured date : </strong> {result.vehicle_manufactured_date}
                        </li>
                        <li>
                          <strong>Vehicle number of cylinders : </strong> {result.vehicle_number_of_cylinders}
                        </li>
                        <li>
                          <strong>Vehicle seating capacity : </strong> {result.insurance?.vehicle_seating_capacity}
                        </li>
                        <li>
                          <strong>Month year remaining for insurance exp : </strong>{' '}
                          {result.insurance?.month_year_remaining_for_insurance_exp}
                        </li>
                        <li>
                          <strong>City : </strong> {result.insurance?.city}
                        </li>
                        <li>
                          <strong>State : </strong> {result.state}
                        </li>
                      </ul>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </Box>
      </Modal>
    </div>
  );
};

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Object.assign(leadActions.default), dispatch),
  };
}

export default connect(null, mapDispatchToProps)(RcfromChessicReport);
